<template>
  <lf-modal :title="'Set Offer Fields'" :close="closeModal" large>
    <template #type>
      <div class="rounded-sm bg-gray-100 px-2 py-1-5">
        {{ template.name }}
      </div>

      <div v-if="funder" class="rounded-sm bg-gray-100 px-2 py-1-5">
        {{ funder.full_name }}
      </div>
    </template>
    <template #content>
      <div class="mt-2 flex flex-col bg-gray-100 text-gray-500">
        <div class="flex px-6 pt-6 pb-3">
          <lf-tab
            v-for="(label, viewId) in VIEWS"
            :key="viewId"
            class="stage-tab group"
            :active="view === viewId"
            @click="selectView(viewId)"
          >
            {{ label }}
          </lf-tab>
        </div>
        <div class="mb-4 flex justify-between px-6">
          <span class="py-4">
            {{ Descriptions[view] }}
          </span>
          <add-group-button v-if="!isBpSection" @add-new-group="addNewGroup" />
        </div>
        <wfb-offer-bp-settings
          v-if="isBpSection"
          :fields="bpOfferVisibleFields"
          :template="template"
          :funder="funder"
          @update:fields="updateFields"
        />
        <div
          v-else
          id="offer-fields-modal"
          class="max-h-70-screen space-y-5 overflow-y-auto px-6 pb-6"
        >
          <fields-group
            v-for="group in localTemplateValue.content.groups"
            :key="group.id"
            :field-group="group"
            :available-fields="avaialbeFields"
            :can-change="canChangeGroup(group.id)"
            @remove-group="removeGroup(group.id)"
            @update-group="updateGroup"
          />
        </div>
        <div class="flex justify-end border-t bg-white p-5">
          <modal-buttons
            :on-save="saveTemplateChanges"
            :on-cancel="closeModal"
            :disabled="!canSaveOfferTemplate"
          />
        </div>
      </div>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import cloneDeep from "lodash/cloneDeep";
import diff from "lodash/difference";
import { v4 as generateUuid } from "uuid";

import { OfferTemplateView } from "@/enums/offerTemplates";
import { FieldSection } from "@/enums/offers";
import { OFFER_SCHEMA_VERSION } from "@/helpers/constants/offers";

import AddGroupButton from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/AddGroupButton.vue";
import FieldsGroup from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/FieldsGroup.vue";
import WfbOfferBpSettings from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/WfbOfferBpSettings.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";

import type { IFunder } from "@/models/funders";
import type {
  FieldGroup,
  OfferTemplate,
  OfferTemplateField,
} from "@/models/offers";

const { t } = useI18n();

const NEW_GROUP: FieldGroup = {
  id: "",
  offer: { title: "", weight: 0, visible: true },
  closing: { title: "", weight: 0, visible: true },
  fields: [],
};

const VIEWS = {
  [OfferTemplateView.OFFER]: t("OFFER_CUSTOMISATION.OFFER_DETAILS"),
  [OfferTemplateView.CLOSING]: t("OFFER_CUSTOMISATION.FUNDING_DETAILS"),
  [OfferTemplateView.BP_OFFER_DETAILS]: t(
    "OFFER_CUSTOMISATION.BP_OFFER_DETAILS"
  ),
  [OfferTemplateView.BP_CLOSING_DETAILS]: t(
    "OFFER_CUSTOMISATION.BP_FUNDING_DETAILS"
  ),
};

const Descriptions = {
  [OfferTemplateView.OFFER]: t("OFFER_CUSTOMISATION.OFFER_TAB_DESCRIPTION"),
  [OfferTemplateView.CLOSING]: t("OFFER_CUSTOMISATION.FUNDING_TAB_DESCRIPTION"),
  [OfferTemplateView.BP_OFFER_DETAILS]: t(
    "OFFER_CUSTOMISATION.BP_OFFER_TAB_DESCRIPTION"
  ),
  [OfferTemplateView.BP_CLOSING_DETAILS]: t(
    "OFFER_CUSTOMISATION.BP_FUNDING_TAB_DESCRIPTION"
  ),
};

const props = withDefaults(
  defineProps<{
    template: OfferTemplate;
    funder?: IFunder;
    isWorkflowLive?: boolean;
  }>(),
  { isWorkflowLive: false }
);

const emit = defineEmits<{
  close: [void];
  templateUpdated: [OfferTemplate];
}>();

const { getters } = useStore();

const newGroups = ref<string[]>([]);

const localTemplateValue = ref(cloneDeep(props.template));
const view = ref<OfferTemplateView>(OfferTemplateView.OFFER);
provide("offerTemplateView", view);

const getCurrentSection = (currentView: OfferTemplateView): FieldSection =>
  currentView === OfferTemplateView.BP_OFFER_DETAILS
    ? FieldSection.OFFER
    : FieldSection.CLOSING;

const isBpSection = computed(() =>
  [
    OfferTemplateView.BP_CLOSING_DETAILS,
    OfferTemplateView.BP_OFFER_DETAILS,
  ].includes(view.value)
);

const getVisibleFields = (
  groups: FieldGroup[],
  section: FieldSection
): OfferTemplateField[] => {
  return groups
    .filter((group) => group.offer.visible || group.closing.visible)
    .flatMap((group) => group.fields)
    .filter((field) => field[section].visibility?.borrower_platform);
};

const bpOfferVisibleFields = computed(() => {
  if (!isBpSection.value) {
    return [];
  }
  return getVisibleFields(
    localTemplateValue.value.content.groups,
    getCurrentSection(view.value)
  );
});

const supportedFieldTypes = computed<Record<string, OfferTemplateField>>(
  () => getters["options/significantOfferFields"]
);

const canSaveOfferTemplate = computed(() => {
  const allGroups = localTemplateValue.value.content.groups;

  const groupHasFields = allGroups.some((group) => !!group.fields.length);
  const groupHasVisibleOfferFields = allGroups.some(
    (group) => group.offer.visible
  );
  const groupHasVisibleClosingFields = allGroups.some(
    (group) => group.closing.visible
  );
  return (
    groupHasFields && groupHasVisibleOfferFields && groupHasVisibleClosingFields
  );
});

const avaialbeFields = computed<Record<string, OfferTemplateField>>(() => {
  const usedFields = localTemplateValue.value.content.groups.reduce<string[]>(
    (acc, group) => {
      group.fields.forEach((field) => acc.push(field.name));
      return acc;
    },
    []
  );
  return diff(Object.keys(supportedFieldTypes.value), usedFields).reduce(
    (acc, key) => {
      return { ...acc, [key]: supportedFieldTypes.value[key] };
    },
    {}
  );
});

const canChangeGroup = (id: string) => {
  return !props.isWorkflowLive || newGroups.value.includes(id);
};

const addNewGroup = () => {
  const id = generateUuid();
  newGroups.value.push(id);

  localTemplateValue.value.content.groups.push({
    ...NEW_GROUP,
    id,
  });
};

const removeGroup = (id: string) => {
  newGroups.value = newGroups.value.filter((item) => item !== id);

  localTemplateValue.value.content.groups =
    localTemplateValue.value.content.groups.filter((group) => group.id !== id);
};

const updateGroup = (updatedGroup: FieldGroup) => {
  localTemplateValue.value.content.groups =
    localTemplateValue.value.content.groups.map((group) =>
      group.id === updatedGroup.id ? updatedGroup : group
    );
};

const updateFields = (updatedFields: OfferTemplateField[]) => {
  localTemplateValue.value.content.groups =
    localTemplateValue.value.content.groups.map((group) => ({
      ...group,
      fields: group.fields.map((field) => {
        const updatedField = updatedFields.find((f) => f.id === field.id);
        return updatedField ?? field;
      }),
    }));
};

const closeModal = () => emit("close");
const selectView = (newView: OfferTemplateView) => (view.value = newView);

const saveTemplateChanges = async () => {
  localTemplateValue.value.content.version = OFFER_SCHEMA_VERSION;

  emit("templateUpdated", localTemplateValue.value);
};
</script>
